<template>
  <v-dialog v-model="dialog"
            fullscreen
            hide-overlay
            scrollable
            activator="#btn_select_content"
            transition="dialog-bottom-transition"
            @click:outside.prevent="close">
    <v-card class="CL_ContentPickerDialog AppComposeCard">
      <v-card-title>
        Select files to share
      </v-card-title>
      <v-card-actions class="d-block px-3">
        <div class="d-flex mb-2">
          <v-menu v-if="team.shared_link_thumbnails_enabled"
                  offset-y>
            <template v-slot:activator="{on, attrs}">
              <v-btn color="primary"
                     :disabled="!canInsertLinks"
                     class="mr-2"
                     v-bind="attrs"
                     v-on="on">
                Insert {{ form.presentations.length | pluralize('link') }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click.prevent="submit(false)">
                <v-list-item-title>
                  Insert links
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click.prevent="submit(true)">
                <v-list-item-title>
                  Insert links with thumbnails
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn v-else
                 @click.prevent="submit(false)"
                 color="primary"
                 :disabled="!canInsertLinks"
                 class="mr-2">
            Insert {{ form.presentations.length | pluralize('link') }}
          </v-btn>
          <v-btn @click.prevent="cancelInsertLinks">
            Cancel
          </v-btn>
        </div>
      </v-card-actions>
      <v-divider></v-divider>
      <v-card-text class="d-flex pa-1">
        <folder-drawer class="mr-1">
        </folder-drawer>
        <div style="width: 100%;">
          <h2 v-if="folder"
              class="d-flex align-center my-2">
            <v-icon left>
              {{ folder.icon ?? 'fas fa-folder-open '}}
            </v-icon>
            {{ folder.name }}: {{ numFiles }}
          </h2>
          <v-divider class="mb-2">
          </v-divider>
          <template v-if="filteredPresentations.length > 0">
            <div class="mb-2">
              <v-checkbox v-model="selectAll"
                          @click.prevent="toggleAll"
                          label="Select all"
                          hide-details
                          class="my-0">
              </v-checkbox>
            </div>
            <v-divider class="mb-2">
            </v-divider>
            <v-alert v-if="templatePresentationIDs.length > 0"
                     type="info"
                     flat
                     tile
                     dense
                     class="mb-1">
              Some files may be hidden because you're only allowed to share specific files with the <strong>{{ template.name }}</strong> template.
            </v-alert>
            <div class="PresentationsGrid">
              <v-card v-for="presentation in filteredPresentations"
                      :key="presentation.id"
                      flat
                      tile
                      class="mx-1 my-2 Presentation__Card">
                <v-img v-if="presentation.thumbnail"
                       :src="presentation.thumbnail.url"
                       max-width="230"
                       max-height="110"
                       @click.prevent="togglePresentation(presentation.id)"
                       alt="Thumbnail"
                       class="Presentation__Image">
                </v-img>
                <div v-else
                     class="d-flex justify-center align-center pa-8">
                  Missing Thumbnail
                </div>
                <v-card-text class="px-0 pt-1 pb-0">
                  <v-checkbox v-model="form.presentations"
                              multiple
                              :value="presentation.id"
                              class="mt-0"
                              hide-details>
                    <template v-slot:label>
                      {{ presentation.name }}
                    </template>
                    <template v-if="presentation.alias && presentation.alias !== presentation.name"
                              v-slot:append>
                      <div class="">
                        <v-tooltip top
                                   color="primary"
                                   content-class="Tooltip__Opacity-1"
                                   max-width="50%"
                                   :open-on-click="true"
                                   :open-on-hover="false"
                                   :open-on-focus="false">
                          <template v-slot:activator="{on, attrs}">
                            <v-btn icon
                                   v-bind="attrs"
                                   v-on="on">
                              <v-icon>
                                fal fa-info-circle
                              </v-icon>
                            </v-btn>
                          </template>
                          <span>{{ presentation.alias }}</span>
                        </v-tooltip>
                      </div>
                    </template>
                  </v-checkbox>
                </v-card-text>
              </v-card>
            </div>
          </template>
          <template v-else>
            <v-alert v-if="isHome"
                     type="info"
                     class="ml-3"
                     outlined
                     text>
              <div v-if="rootFolders.length">
                There are no shareable files here. Select a folder on the left.
              </div>
              <div v-else>
                There are no shareable files here.
              </div>
            </v-alert>
            <v-alert type="info"
                     class="ml-3"
                     outlined
                     text
                     v-else-if="isBookmarks">
              You don't have any shareable favorites.
            </v-alert>
            <v-alert type="info"
                     class="ml-3"
                     outlined
                     text
                     v-else>
              <div v-if="folder && folder.children.length">
                There are no shareable files in this folder. Select a different folder or subfolder on the left.
              </div>
              <div v-else
                   class="ml-4">
                There are no shareable files in this folder. Select a different folder on the left.
              </div>
            </v-alert>
          </template>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import {mapGetters} from 'vuex';
import FolderDrawer from '../../../content/components/FolderDrawer';
import pluralize from 'pluralize';
import {clone, isArray, isInteger, isObject} from 'lodash';

export default {
  components: {FolderDrawer},
  props: {
    form: {
      type: Object,
      required: true,
    },
    dialog: {
      type: Boolean,
      required: true,
    }
  },
  watch: {
    folder: function (val) {
      let allSelected = true;
      this.filteredPresentations.forEach(p => {
        if (!this.form?.presentations?.includes(p.id)) {
          allSelected = false;
        }
      })
      this.selectAll = allSelected;
    },
  },
  data() {
    return {
      filter: {
        name: '',
        type: '',
      },
      selectAll: false,
    }
  },
  computed: {
    ...mapGetters([
      'isAdmin',
      'isMember',
      'team',
      'user',
    ]),
    ...mapGetters('content', [
      'bookmarkedPresentations',
      'folder',
      'isWorld', // MLD-7739
      'isHome',
      'isBookmarks', // MLD-2107
      'isMyFolder', // MLD-2729
      'presentations',
      'rootFolders',
      'sortOptions',
    ]),
    ...mapGetters('emailCart', [ // MLD-3776
      'template',
      'templatePresentationIDs',
    ]),
    numFiles() {
      return `${this.filteredPresentations.length} Shareable ${pluralize('File', this.filteredPresentations.length, false)}`
      //return pluralize('File', this.filteredPresentations.length, true);
    },
    canInsertLinks() {
      return this.presentations.length > 0;
    },
    filteredPresentations() {
      return this.presentations.filter(p => {
        if (p.can_be_shared_email === false) { // MLD-4664
          return false;
        }
        if (this.filter.name && p.name.toLowerCase().indexOf(this.filter.name.toLowerCase()) === -1) {
          return false;
        }
        if (this.filter.type && p.presentation_type !== this.filter.type) {
          return false;
        }
        if (isArray(this.templatePresentationIDs) && this.templatePresentationIDs.length > 0 && this.templatePresentationIDs.includes(p.id) === false) { // MLD-3776 Genmab uses this, updated in MLD-6838
          return false;
        }
        if (isObject(this.template)
          && isInteger(this.template.id)
          && isInteger(p.share_template_id)
          && p.is_template_required === true // MLD-7199
          && p.share_template_id !== this.template.id
        ) { // MLD-6838 If the presentation has an associated template, and it's not the current template. don't show the presentation.
          return false;
        }
        if (this.isWorld) { // MLD-7739
          return p;
        } else if (this.isHome) {
          return p.show_in_main_list;
        } else if (this.isBookmarks) {
          return !!this.bookmarkedPresentations?.includes(p.id);
        } else if (this.isMyFolder && this.user) {
          return p.owner_id === this.user.id;
        } else {
          return !!this.folder?.presentation_ids?.includes(p.id);
        }
      })
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData() {
    },
    close() {
      //console.log('close()');
      this.$emit('close');
      //this.dialog = false;
    },
    cancelInsertLinks() {
      this.form.presentations = [];
      this.close();
    },
    togglePresentation(presentationID) {
      if (this.form?.presentations?.includes(presentationID)) {
        //console.log(`Removing ${presentationID}`);
        this.form.presentations = this.form.presentations.filter(id => id !== presentationID);
      } else {
        this.form.presentations.push(presentationID);
      }
    },
    toggleAll() {
      if (this.selectAll) {
        this.filteredPresentations.forEach(p => {
          if (!!this.form?.presentations?.includes(p.id) === false) {
            this.form.presentations.push(p.id);
          }
        })
      } else {
        let items = clone(this.form.presentations);
        this.filteredPresentations.forEach(p => {
          items = items.filter(id => id !== p.id);
        })
        this.form.presentations = items;
      }
    },
    submit(includeImages) { // MLD-3113
      this.$emit('insert', includeImages);
    }
  }
}
</script>
